import React, { createContext, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TourGuide from './TourGuide';
import useTour from '../hooks/useTour';

const TourContext = createContext();

export const useTourContext = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTourContext must be used within a TourProvider');
  }
  return context;
};

const TourProvider = ({ children }) => {
  const location = useLocation();
  const [currentTour, setCurrentTour] = useState(null);
  const [tourSteps, setTourSteps] = useState([]);
  
  const tour = useTour(tourSteps);

  // Define tours for different screens
  const screenTours = {
    'dashboard': {
      steps: [
        {
          target: '.side-menu',
          title: 'Menú Principal',
          content: 'Aquí encontrarás acceso a todas las secciones principales de Bankivo: Gestión Comercial, Logística, Configuración y más.'
        },
        {
          target: '.app-navbar',
          title: 'Barra de Navegación',
          content: 'La barra superior contiene información de la sesión, notificaciones y herramientas de usuario.'
        },
        {
          target: '.main-grid',
          title: 'Dashboard Principal',
          content: 'Este es tu centro de control donde puedes ver estadísticas importantes, accesos rápidos y resúmenes de tu negocio.'
        }
      ]
    },
    'crear-producto-financiero': {
      steps: [
        {
          target: '[data-tour="datos-generales"]',
          title: 'Datos Generales',
          content: 'Completa la información básica del producto financiero: nombre, tipo, montos mínimos y máximos, y configuración de sucursales.'
        },
        {
          target: '[data-tour="config-mora"]',
          title: 'Configuración de Mora',
          content: 'Define cómo se calculará la mora para este producto: tipo de mora, valor, días de gracia y frecuencia de cobro.'
        },
        {
          target: '[data-tour="config-interes"]',
          title: 'Configuración de Interés',
          content: 'Establece el tipo de interés (fija, variable, compuesto, etc.) y los valores correspondientes.'
        },
        {
          target: '[data-tour="campos-formulario"]',
          title: 'Campos del Formulario',
          content: 'Personaliza los campos que aparecerán en la solicitud de este producto financiero. Puedes agregar, editar y reordenar campos por categorías.'
        },
        {
          target: '[data-tour="guardar-producto"]',
          title: 'Guardar Producto',
          content: 'Una vez completada toda la información, haz clic en "Guardar" para crear el producto financiero.'
        }
      ]
    },
    'productos-financieros': {
      steps: [
        {
          target: '.side-menu',
          title: 'Gestión de Productos',
          content: 'Esta sección te permite administrar todos los productos financieros de tu empresa.'
        },
        {
          target: '[data-tour="lista-productos"]',
          title: 'Lista de Productos',
          content: 'Aquí puedes ver todos los productos financieros creados, editarlos o crear nuevos.'
        }
      ]
    },
    'prestamos': {
      steps: [
        {
          target: '[data-tour="seleccionar-producto-inicial"]',
          title: 'Seleccionar Producto Financiero',
          content: 'Primero debes seleccionar un producto financiero para continuar con la creación del préstamo.'
        },
        {
          target: '[data-tour="producto-financiero"]',
          title: 'Información del Producto',
          content: 'Aquí puedes ver los detalles del producto financiero seleccionado: montos mínimos/máximos, intereses y configuración de mora.'
        },
        {
          target: '[data-tour="seleccion-inventario"]',
          title: 'Selección de Inventario',
          content: 'Si el producto requiere inventario, selecciona el producto específico que será financiado y configura el monto inicial.'
        },
        {
          target: '[data-tour="formulario-datos-personales"]',
          title: 'Datos del Cliente',
          content: 'Completa la información personal del cliente: identificación, nombre, teléfono, dirección y otros datos requeridos.'
        },
        {
          target: '[data-tour="formulario-datos-del-préstamo"]',
          title: 'Datos del Préstamo',
          content: 'Configura los detalles del préstamo: monto solicitado, plazo, frecuencia de pago, interés y mora.'
        },
        {
          target: '[data-tour="acciones-prestamo"]',
          title: 'Crear Préstamo',
          content: 'Revisa el resumen de cuotas y deuda total, luego haz clic en "Guardar" para crear el préstamo o solicitud.'
        }
      ]
    },
    'centro-cobros': {
      steps: [
        {
          target: '.side-menu',
          title: 'Centro de Cobros',
          content: 'Administra pagos, cargos y cobros de manera eficiente.'
        },
        {
          target: '[data-tour="cargos-pendientes"]',
          title: 'Cargos Pendientes',
          content: 'Revisa y gestiona los cargos que están pendientes de pago.'
        }
      ]
    },
    'gestion-comercial': {
      steps: [
        {
          target: '.side-menu',
          title: 'Gestión Comercial',
          content: 'Módulo completo para gestionar productos financieros, préstamos y cobros.'
        },
        {
          target: '[data-tour="menu-gestion"]',
          title: 'Menú de Gestión',
          content: 'Accede a todas las herramientas de gestión comercial desde aquí.'
        }
      ]
    },
    'logistica': {
      steps: [
        {
          target: '[data-tour="boton-agregar-categoria"]',
          title: 'Crear Nueva Categoría',
          content: 'Haz clic en "Agregar categoría" para crear una nueva categoría de productos y organizar tu inventario.'
        },
        {
          target: '[data-tour="formulario-agregar-categoria"]',
          title: 'Formulario de Categoría',
          content: 'Completa el nombre de la categoría y selecciona el tipo de inventario (Serializado o Genérico).'
        },
        {
          target: '[data-tour="campos-personalizados"]',
          title: 'Campos Personalizados',
          content: 'Define los campos específicos para esta categoría. Puedes agregar campos, cambiar tipos, y reordenarlos arrastrando.'
        },
        {
          target: '[data-tour="acciones-categoria"]',
          title: 'Guardar Categoría',
          content: 'Una vez completados todos los campos, haz clic en "Guardar Categoría" para crear la nueva categoría.'
        },
        {
          target: '[data-tour="categorias-existentes"]',
          title: 'Categorías Existentes',
          content: 'Aquí puedes ver todas las categorías creadas y editarlas haciendo clic en la flecha hacia abajo.'
        }
      ]
    },

    'productos': {
      steps: [
        {
          target: '[data-tour="seleccionar-categoria"]',
          title: 'Seleccionar Categoría',
          content: 'Primero selecciona una categoría de producto para continuar. Esto determinará qué campos aparecerán en el formulario.'
        },
        {
          target: '[data-tour="formulario-principal"]',
          title: 'Formulario Principal',
          content: 'Completa la información básica del producto: nombre, descripción, precio, costo y almacén donde se guardará.'
        },
        {
          target: '[data-tour="campos-personalizados"]',
          title: 'Campos Personalizados',
          content: 'Completa los campos específicos definidos para esta categoría. Cada categoría puede tener campos únicos.'
        },
        {
          target: '[data-tour="listas-de-precios"]',
          title: 'Listas de Precios',
          content: 'Asocia el producto a diferentes listas de precios y define el valor específico para cada una.'
        },
        {
          target: '[data-tour="resumen-producto"]',
          title: 'Resumen del Producto',
          content: 'Revisa el resumen de toda la información ingresada antes de guardar el producto.'
        },
        {
          target: '[data-tour="acciones-guardar"]',
          title: 'Guardar Producto',
          content: 'Guarda el producto o guárdalo y agrega otro. También puedes cancelar si no deseas guardar.'
        }
      ]
    },
    'configuracion': {
      steps: [
        {
          target: '.side-menu',
          title: 'Configuración',
          content: 'Configura usuarios, permisos y parámetros del sistema.'
        },
        {
          target: '[data-tour="config-usuarios"]',
          title: 'Configuración de Usuarios',
          content: 'Administra usuarios y sus permisos en el sistema.'
        }
      ]
    }
  };

  // Function to determine tour type based on current path
  const getTourTypeFromPath = (pathname) => {
    if (pathname.includes('/productos_financieros/crear')) {
      return 'crear-producto-financiero';
    } else if (pathname.includes('/productos_financieros')) {
      return 'productos-financieros';
    } else if (pathname.includes('/prestamos')) {
      return 'prestamos';
    } else if (pathname.includes('/centro_cobros')) {
      return 'centro-cobros';
    } else if (pathname.includes('/gestion_comercial')) {
      return 'gestion-comercial';
    } else if (pathname.includes('/logistica')) {
      return 'logistica';
    } else if (pathname.includes('/configuracion')) {
      return 'configuracion';
    } else if (pathname.includes('/dashboard')) {
      return 'dashboard';
    }
    return 'dashboard'; // default
  };

  // Function to start tour for current screen
  const startCurrentScreenTour = () => {
    const tourType = getTourTypeFromPath(location.pathname);
    const tourConfig = screenTours[tourType];
    
    if (tourConfig) {
      setCurrentTour(tourType);
      setTourSteps(tourConfig.steps);
      tour.startTour();
    }
  };

  // Function to start specific tour
  const startSpecificTour = (tourType) => {
    const tourConfig = screenTours[tourType];
    
    if (tourConfig) {
      setCurrentTour(tourType);
      setTourSteps(tourConfig.steps);
      tour.startTour();
    }
  };

  // Clear current tour
  const clearTour = () => {
    setCurrentTour(null);
    setTourSteps([]);
  };

  // Handle tour close
  const handleTourClose = () => {
    tour.closeTour();
    clearTour();
  };

  // Handle tour complete
  const handleTourComplete = () => {
    tour.completeTour();
    clearTour();
  };

  const value = {
    currentTour,
    startCurrentScreenTour,
    startSpecificTour,
    clearTour,
    screenTours
  };

  return (
    <TourContext.Provider value={value}>
      {children}
      
      {/* Global Tour Guide */}
      {currentTour && (
        <TourGuide
          steps={tour.steps}
          isOpen={tour.isOpen}
          onClose={handleTourClose}
          onComplete={handleTourComplete}
          showProgress={true}
          showSkip={true}
        />
      )}
    </TourContext.Provider>
  );
};

export default TourProvider;
